import type { DealParticipant, SynchronizationStatus, SynchronizedWith } from '@openx/types/deal';
import { DV360_DEMAND_PARTNER_ID, TTD_DEMAND_PARTNER_ID } from '@openx/utils/services/config';
import { useTranslation } from 'react-i18next';

import {
  getDspStatus,
  getDV360Status,
  getStatusColor,
  getTTDStatus,
  hasDv360FailureReason,
  hasDv360OrderName,
  hasDv360RejectReason,
  hasDv360Seller,
  hasTtdProposalId,
  isValidDMP,
  mapDV360FailureReasonToMessage,
  mapDV360StatusToMessage,
  mapTTDStatusToMessage,
} from './utils';

export type UseGetDspStatus = {
  dv360Message: string;
  dv360ErrorReason: string;
  ttdMessage: string;
  dv360Status: SynchronizationStatus | null;
  ttdStatus: SynchronizationStatus | null;
  dspStatus: SynchronizationStatus;
  statusColor: string;
  shouldDisplayStatus: boolean;
};

type UseGetDspStatusArgs = {
  sync?: SynchronizedWith | null;
  deal_participants?: DealParticipant[];
  isOxsSubOrg?: boolean;
};

export const useGetDspStatus = ({ sync, deal_participants, isOxsSubOrg }: UseGetDspStatusArgs): UseGetDspStatus => {
  const { t } = useTranslation();

  const isValidDV360Dmp = isValidDMP(deal_participants ?? [], DV360_DEMAND_PARTNER_ID);
  const isValidTtdDmp = isValidDMP(deal_participants ?? [], TTD_DEMAND_PARTNER_ID);

  const hasDV360 = hasDv360Seller(sync);
  const hasTTD = hasTtdProposalId(sync);

  const isDV360Synced = hasDv360OrderName(sync) && !hasDv360RejectReason(sync) && !hasDv360FailureReason(sync);
  const isDV360Rejected = hasDv360RejectReason(sync) && !hasDv360FailureReason(sync);
  const isDV360Failed = hasDv360FailureReason(sync);
  const dv360Status = isValidDV360Dmp ? getDV360Status(isDV360Synced, isDV360Rejected, isDV360Failed) : null;

  const ttdStatus = isValidTtdDmp ? getTTDStatus(hasTTD, sync?.ttd_proposal_id?.toString() !== '-1') : null;

  const dspStatus = getDspStatus(dv360Status, ttdStatus);
  const statusColor = getStatusColor(dspStatus);

  const dv360Message = (isValidDV360Dmp && mapDV360StatusToMessage(t, dv360Status, sync?.dv360seller)) || '';

  const dv360FailureReason =
    (isValidDV360Dmp && mapDV360FailureReasonToMessage(t, sync?.dv360seller?.failure_reason)) || '';

  const dv360RejectReason =
    (isValidDV360Dmp && t('Reason: {reason}', { reason: sync?.dv360seller?.reject_reason })) || '';

  const dv360ErrorReason =
    (hasDv360FailureReason(sync) && dv360FailureReason) || (hasDv360RejectReason(sync) && dv360RejectReason) || '';

  const ttdMessage = (isValidTtdDmp && mapTTDStatusToMessage(t, ttdStatus, sync?.ttd_proposal_id?.toString())) || '';

  const shouldDisplayStatus = !isOxsSubOrg && ((isValidDV360Dmp && hasDV360) || (isValidTtdDmp && hasTTD));

  return {
    dspStatus,
    dv360ErrorReason,
    dv360Message,
    dv360Status,
    shouldDisplayStatus,
    statusColor,
    ttdMessage,
    ttdStatus,
  };
};
